// Need to use the React-specific entry point to import createApi
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import { openSnackbar } from './notify';
import { displayBackdrop, hideBackdrop } from './loading';

const API_URL = process.env.REACT_APP_API_URL;

// Define a service using a base URL and expected endpoints
export const categoryService = createApi({
    reducerPath: 'categoryService',
    baseQuery: fetchBaseQuery({ baseUrl: API_URL }),
    tagTypes: ['CATEGORY'],
    endpoints: (builder) => ({
        getCategoryList: builder.query({
            query: () => `category`,
            providesTags: [{ type: 'CATEGORY', id: 'all-category' }],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                dispatch(displayBackdrop());
                try {
                    const { data } = await queryFulfilled;
                    if(data){
                        dispatch(hideBackdrop());
                    }
                } catch (err) {
                    const { error } = err;
                    dispatch(hideBackdrop());
                    dispatch(openSnackbar({ options: { variant: "error" }, message: error?.data?.message || 'Something went wrong, Please try again.' }));
                }
            },
            transformResponse: (response) => response.data,
        }),
        getCategoryObj: builder.query({
            query: (categoryKey) => `category/${categoryKey}`,
            providesTags: (result, error, arg) => [{ type: 'CATEGORY', id: arg }],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                dispatch(displayBackdrop());
                try {
                    const { data } = await queryFulfilled;
                    if(data){
                        dispatch(hideBackdrop());
                    }
                } catch (err) {
                    const { error } = err;
                    dispatch(hideBackdrop());
                    dispatch(openSnackbar({ options: { variant: "error" }, message: error?.data?.message || 'Something went wrong, Please try again.' }));
                }
            },
            transformResponse: (response) => response.data,
        }),
        createCategory: builder.mutation({
            query: (initialPost) => ({ url: 'category', method: 'POST', body: initialPost }),
            invalidatesTags: [{ type: 'CATEGORY', id: 'all-category' }],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                dispatch(displayBackdrop());
                try {
                    const { data } = await queryFulfilled;
                    if(data){
                        dispatch(hideBackdrop());
                    }
                } catch (err) {
                    const { error } = err;
                    dispatch(hideBackdrop());
                    dispatch(openSnackbar({ options: { variant: "error" }, message: error?.data?.message || 'Something went wrong, Please try again.' }));
                }
            },
        }),
        updateCategory: builder.mutation({
            query: ({categoryKey, initialPost}) => ({ url: `category/${categoryKey}`, method: 'PUT', body: initialPost }),
            invalidatesTags: (result, error, arg) => [{ type: 'CATEGORY', id: arg.categoryKey }],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                dispatch(displayBackdrop());
                try {
                    const { data } = await queryFulfilled;
                    if(data){
                        dispatch(hideBackdrop());
                    }
                } catch (err) {
                    const { error } = err;
                    dispatch(hideBackdrop());
                    dispatch(openSnackbar({ options: { variant: "error" }, message: error?.data?.message || 'Something went wrong, Please try again.' }));
                }
            },
        }),
        updateCategoryPopular: builder.mutation({
            query: ({categoryKey, initialPost}) => ({ url: `category-popular/${categoryKey}`, method: 'PUT', body: initialPost }),
            invalidatesTags: (result, error, arg) => [{ type: 'CATEGORY', id: arg.categoryKey }, { type: 'CATEGORY', id: 'all-category' }],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                dispatch(displayBackdrop());
                try {
                    const { data } = await queryFulfilled;
                    if(data){
                        dispatch(hideBackdrop());
                    }
                } catch (err) {
                    const { error } = err;
                    dispatch(hideBackdrop());
                    dispatch(openSnackbar({ options: { variant: "error" }, message: error?.data?.message || 'Something went wrong, Please try again.' }));
                }
            },
        }),
        deleteCategory: builder.mutation({
            query: (categoryKey) => ({ url: `category/${categoryKey}`, method: 'DELETE' }),
            invalidatesTags: [{ type: 'CATEGORY', id: 'all-category' }],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                dispatch(displayBackdrop());
                try {
                    const { data } = await queryFulfilled;
                    if(data){
                        dispatch(hideBackdrop());
                    }
                } catch (err) {
                    const { error } = err;
                    dispatch(hideBackdrop());
                    dispatch(openSnackbar({ options: { variant: "error" }, message: error?.data?.message || 'Something went wrong, Please try again.' }));
                }
            },
        }),
        getSiteCategoryObj: builder.query({
            query: (categoryKey) => `site/category/${categoryKey}`,
            providesTags: (result, error, arg) => [{ type: 'CATEGORY', id: arg }],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                dispatch(displayBackdrop());
                try {
                    const { data } = await queryFulfilled;
                    if(data){
                        dispatch(hideBackdrop());
                    }
                } catch (err) {
                    const { error } = err;
                    dispatch(hideBackdrop());
                    dispatch(openSnackbar({ options: { variant: "error" }, message: error?.data?.message || 'Something went wrong, Please try again.' }));
                }
            },
            transformResponse: (response) => response.data,
        }),
        getSiteCategoryPopularObj: builder.query({
            query: (categoryKey) => `site/category-popular/`,
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                dispatch(displayBackdrop());
                try {
                    const { data } = await queryFulfilled;
                    if(data){
                        dispatch(hideBackdrop());
                    }
                } catch (err) {
                    const { error } = err;
                    dispatch(hideBackdrop());
                    dispatch(openSnackbar({ options: { variant: "error" }, message: error?.data?.message || 'Something went wrong, Please try again.' }));
                }
            },
            transformResponse: (response) => response.data,
        }),
    }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const { useGetCategoryListQuery, useGetCategoryObjQuery, useCreateCategoryMutation, useUpdateCategoryMutation, useUpdateCategoryPopularMutation, useDeleteCategoryMutation, useGetSiteCategoryObjQuery, useGetSiteCategoryPopularObjQuery } = categoryService;